import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Send, ArrowLeft, Plus, Trash2, BookOpen, User, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';

const DiscussionBoard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [discussions, setDiscussions] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDiscussion, setNewDiscussion] = useState({ course_id: '', title: '' });
  const [creating, setCreating] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!user || !['student', 'teacher'].includes(user.role)) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    try {
      const [discussionsRes, coursesRes] = await Promise.all([
        api.get('/discussions'),
        user.role === 'teacher' ? api.get('/teachers/courses') : api.get('/enrollments')
      ]);
      setDiscussions(discussionsRes.data);
      
      if (user.role === 'teacher') {
        setCourses(coursesRes.data);
      } else {
        // For students, extract course info from enrollments
        const enrolledCourses = coursesRes.data.map(e => ({
          id: e.course_id,
          title: e.course_title || e.title
        }));
        setCourses(enrolledCourses);
      }
    } catch (error) {
      
      toast.error('Failed to load discussions');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscussionMessages = async (discussionId) => {
    try {
      const response = await api.get(`/discussions/${discussionId}`);
      setSelectedDiscussion(response.data.discussion);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleCreateDiscussion = async (e) => {
    e.preventDefault();
    if (!newDiscussion.course_id || !newDiscussion.title.trim()) {
      toast.error('Please select a course and enter a title');
      return;
    }
    setCreating(true);
    try {
      await api.post('/discussions', newDiscussion);
      toast.success('Discussion created!');
      setCreateDialogOpen(false);
      setNewDiscussion({ course_id: '', title: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discussion');
    } finally {
      setCreating(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedDiscussion) return;
    
    setSendingMessage(true);
    try {
      const response = await api.post(`/discussions/${selectedDiscussion.id}/messages`, {
        content: newMessage.trim()
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteDiscussion = async (discussionId) => {
    if (!confirm('Are you sure you want to delete this discussion?')) return;
    try {
      await api.delete(`/discussions/${discussionId}`);
      toast.success('Discussion deleted');
      if (selectedDiscussion?.id === discussionId) {
        setSelectedDiscussion(null);
        setMessages([]);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete discussion');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(user.role === 'teacher' ? '/teacher/dashboard' : '/student/dashboard')}
                className="text-white hover:bg-white/20"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-6 h-6" />
                <h1 className="text-xl font-bold">Discussion Board</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-violet-200">{user?.name}</span>
              <span className="px-2 py-1 bg-white/20 rounded-full text-xs uppercase">{user?.role}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-180px)]">
          {/* Discussions List */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="discussions-list">
            <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Discussions</h2>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-violet-600 hover:bg-violet-700" data-testid="new-discussion-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white" data-testid="create-discussion-dialog">
                  <DialogHeader>
                    <DialogTitle>Start New Discussion</DialogTitle>
                    <DialogDescription>
                      Create a discussion thread for a course
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateDiscussion} className="space-y-4 mt-4">
                    <div>
                      <Label>Course</Label>
                      <Select 
                        value={newDiscussion.course_id} 
                        onValueChange={(val) => setNewDiscussion({...newDiscussion, course_id: val})}
                      >
                        <SelectTrigger className="mt-1" data-testid="select-course">
                          <SelectValue placeholder="Select a course" />
                        </SelectTrigger>
                        <SelectContent>
                          {courses.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Discussion Title</Label>
                      <Input
                        value={newDiscussion.title}
                        onChange={(e) => setNewDiscussion({...newDiscussion, title: e.target.value})}
                        placeholder="e.g., Question about Lesson 3"
                        className="mt-1"
                        data-testid="discussion-title-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" disabled={creating} data-testid="create-discussion-submit">
                      {creating ? 'Creating...' : 'Create Discussion'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <ScrollArea className="h-[calc(100%-60px)]">
              {discussions.length === 0 ? (
                <div className="p-6 text-center text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No discussions yet</p>
                  <p className="text-sm mt-1">Start a new discussion to get help</p>
                </div>
              ) : (
                <div className="divide-y">
                  {discussions.map((disc) => (
                    <div
                      key={disc.id}
                      onClick={() => fetchDiscussionMessages(disc.id)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedDiscussion?.id === disc.id ? 'bg-violet-50 border-l-4 border-violet-600' : ''
                      }`}
                      data-testid={`discussion-item-${disc.id}`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-medium text-slate-900 text-sm line-clamp-1">{disc.title}</h3>
                        {(disc.created_by_id === user.id || user.role === 'teacher') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                            onClick={(e) => { e.stopPropagation(); handleDeleteDiscussion(disc.id); }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                        <BookOpen className="w-3 h-3" />
                        <span className="line-clamp-1">{disc.course_title}</span>
                      </div>
                      {disc.last_message_preview && (
                        <p className="text-xs text-slate-600 line-clamp-1">
                          <span className="font-medium">{disc.last_message_sender}:</span> {disc.last_message_preview}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                        <span>{disc.message_count || 0} messages</span>
                        <span>{formatDate(disc.last_message_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Messages Panel */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden flex flex-col" data-testid="messages-panel">
            {selectedDiscussion ? (
              <>
                {/* Discussion Header */}
                <div className="p-4 border-b bg-slate-50">
                  <h2 className="font-semibold text-slate-900">{selectedDiscussion.title}</h2>
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {selectedDiscussion.course_title}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      Started by {selectedDiscussion.created_by_name}
                    </span>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center text-slate-500 py-10">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No messages yet</p>
                        <p className="text-sm">Be the first to send a message!</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              msg.sender_id === user.id
                                ? 'bg-violet-600 text-white rounded-br-md'
                                : 'bg-slate-100 text-slate-900 rounded-bl-md'
                            }`}
                          >
                            {msg.sender_id !== user.id && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium">{msg.sender_name}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  msg.sender_role === 'teacher' 
                                    ? 'bg-violet-100 text-violet-700' 
                                    : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {msg.sender_role}
                                </span>
                              </div>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === user.id ? 'text-violet-200' : 'text-slate-400'
                            }`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <div className="p-4 border-t bg-slate-50">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={sendingMessage || !newMessage.trim()}
                      className="bg-violet-600 hover:bg-violet-700"
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-700">Select a Discussion</h3>
                  <p className="text-sm mt-1">Choose a discussion from the list to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscussionBoard;
